import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { colors } from './index';

// Slideshow modal for reviewing inspection notes/photos — one slide per entry
// ({ sectionTitle, title, note, photos }). Owns its own paging + photo lightbox;
// the parent just renders it conditionally and handles onClose.
export default function NotesSlideshow({ slides, onClose }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  if (!slides?.length) return null;
  const i = Math.min(idx, slides.length - 1);
  const slide = slides[i];

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-40 flex items-center justify-center p-5"
        style={{ background: 'rgba(0,0,0,0.9)' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          className="w-full max-w-md rounded-2xl p-5 flex flex-col"
          style={{ background: colors.cardBg, maxHeight: '85vh' }}
        >
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>
              {slide.sectionTitle}
            </p>
            <button onClick={onClose} className="text-2xl leading-none -mt-1" style={{ color: colors.textMuted }} aria-label="Close">×</button>
          </div>
          <p className="font-medium mb-3" style={{ color: colors.textPrimary }}>{slide.title}</p>
          <div className="overflow-y-auto">
            {(slide.note || '').trim() && (
              <p className="text-sm mb-3" style={{ color: colors.textSecondary, whiteSpace: 'pre-wrap' }}>{slide.note}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {(slide.photos || []).map((photo, k) =>
                photo.url ? (
                  <img
                    key={k}
                    src={photo.url}
                    alt=""
                    onClick={() => setLightbox(photo.url)}
                    className="w-24 h-24 rounded-lg object-cover cursor-pointer"
                    style={{ background: colors.inputBg }}
                  />
                ) : (
                  <div key={k} className="w-24 h-24 rounded-lg flex items-center justify-center text-xs" style={{ background: colors.inputBg, color: colors.yellow }}>
                    Pending
                  </div>
                )
              )}
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: colors.border }}>
            <button
              onClick={() => setIdx(n => Math.max(0, n - 1))}
              disabled={i === 0}
              className="p-2 rounded-lg"
              style={{ background: colors.inputBg, color: colors.textSecondary, opacity: i === 0 ? 0.4 : 1 }}
              aria-label="Previous note"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-xs" style={{ color: colors.textMuted }}>{i + 1} of {slides.length}</span>
            <button
              onClick={() => setIdx(n => Math.min(slides.length - 1, n + 1))}
              disabled={i === slides.length - 1}
              className="p-2 rounded-lg"
              style={{ background: colors.inputBg, color: colors.textSecondary, opacity: i === slides.length - 1 ? 0.4 : 1 }}
              aria-label="Next note"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{ background: 'rgba(0,0,0,0.9)' }}
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
}
